import React, { useCallback, useState } from 'react';
import { CloudArrowUpIcon, VideoCameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useApplyOssDirectUpload, UploadProgress } from '../../hooks/useApplyOssDirectUpload';

interface VideoUploadProps {
  value?: string;
  onChange: (url: string, originalName: string) => void;
  disabled?: boolean;
}

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v'];
const MAX_FILE_SIZE = 220 * 1024 * 1024; // 220MB

export const VideoUpload: React.FC<VideoUploadProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { upload } = useApplyOssDirectUpload({
    prefix: 'fancypeter/apply',
    onProgress: setProgress,
  });

  const validateFile = useCallback((file: File): string | null => {
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      return '只支持 MP4、MOV、M4V 格式';
    }
    if (file.size > MAX_FILE_SIZE) {
      return '视频文件大小不能超过 220MB';
    }
    return null;
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setError(null);
      setUploading(true);

      try {
        const url = await upload(file);
        onChange(url, file.name);
      } catch (err) {
        console.error('Upload failed:', err);
        setError('上传失败，请重试');
      } finally {
        setUploading(false);
        setProgress(null);
      }
    },
    [upload, onChange, validateFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      e.target.value = '';
    },
    [handleFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleRemove = useCallback(() => {
    onChange('', '');
  }, [onChange]);

  if (value) {
    return (
      <div className="space-y-2">
        <div className="relative inline-block">
          <div className="w-40 h-28 rounded-lg overflow-hidden bg-black">
            <video
              src={value}
              controls
              className="w-full h-full"
            />
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-indigo-500 bg-indigo-500/10' : 'border-gray-300 hover:border-indigo-400'}
          ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => {
          if (!disabled && !uploading) {
            document.getElementById('apply-video-upload-input')?.click();
          }
        }}
      >
        <input
          id="apply-video-upload-input"
          type="file"
          accept={ACCEPTED_VIDEO_TYPES.join(',')}
          onChange={handleInputChange}
          disabled={disabled || uploading}
          className="hidden"
        />

        {uploading ? (
          <div className="space-y-2">
            <CloudArrowUpIcon className="w-8 h-8 mx-auto text-indigo-500 animate-bounce" />
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress?.percent || 0}%` }}
              />
            </div>
            <p className="text-xs text-gray-400">
              {progress?.percent || 0}%
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <VideoCameraIcon className="w-8 h-8 mx-auto text-gray-400" />
            <p className="text-sm text-gray-400">
              点击或拖拽上传视频
            </p>
            <p className="text-xs text-gray-500">
              支持 MP4/MOV/M4V，最大 220MB
            </p>
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};
